import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Grid, GridItem, InputText, theme } from '@mainstem/react-mainstem'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch, faMapMarkerAlt, faCheck } from '@fortawesome/pro-light-svg-icons'
import { MainStemApi } from 'api-new'
import type { MainStemAPIPlatformControllersPurchasingAddressAutoCompleteAPIResponseAddressDetails } from 'api-new'
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'components/ui'
import { IAddress } from './types'

interface IModalChangeAddress {
  address: IAddress
  onClose: () => void
  onSave: (address: IAddress) => void
}

const ModalChangeAddress: React.FC<IModalChangeAddress> = ({ address, onClose, onSave }) => {
  const [address1, setAddress1] = useState(address.address1)
  const [address2, setAddress2] = useState(address.address2)
  const [city, setCity] = useState(address.city)
  const [state, setState] = useState(address.state)
  const [zip, setZip] = useState(address.zip)
  const [country, setCountry] = useState(address.country)

  // Address lookup state
  const [searchQuery, setSearchQuery] = useState('')
  const [suggestions, setSuggestions] = useState<
    MainStemAPIPlatformControllersPurchasingAddressAutoCompleteAPIResponseAddressDetails[]
  >([])
  const [searching, setSearching] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)

  const handleSearch = useCallback((query: string) => {
    if (query.trim().length < 3) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }

    setSearching(true)
    MainStemApi.api
      .addressAutoComplete({ query: query.trim() })
      .then((response) => {
        if (response.data.wasSuccessful && response.data.addresses) {
          setSuggestions(response.data.addresses)
          setShowSuggestions(true)
        }
      })
      .catch(() => {
        setSuggestions([])
      })
      .finally(() => {
        setSearching(false)
      })
  }, [])

  const handleQueryChange = (val: string) => {
    setSearchQuery(val || '')
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => handleSearch(val || ''), 350)
  }

  const handleSelectSuggestion = (
    suggestion: MainStemAPIPlatformControllersPurchasingAddressAutoCompleteAPIResponseAddressDetails
  ) => {
    setAddress1(suggestion.address1)
    setAddress2(suggestion.address2)
    setCity(suggestion.city)
    setState(suggestion.state)
    setZip(suggestion.zip)
    setCountry(suggestion.country)
    setShowSuggestions(false)
    setSearchQuery('')
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const handleSave = () => {
    onSave({ address1, address2, city, state, zip, country })
  }

  return (
    <Modal onClose={onClose} size='lg'>
      <ModalHeader onClose={onClose}>Change Ship To Address</ModalHeader>
      <ModalBody>
        <div className='space-y-5'>
          {/* Address Lookup */}
          <div className='relative' ref={suggestionsRef}>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              <FontAwesomeIcon icon={faSearch} className='mr-1.5 text-slate-400' />
              Address Lookup
            </label>
            <div className='relative'>
              <InputText
                onChange={handleQueryChange}
                placeholder='Start typing an address to search...'
                value={searchQuery}
              />
              {searching && (
                <div className='absolute right-3 top-1/2 -translate-y-1/2'>
                  <div className='w-4 h-4 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin' />
                </div>
              )}
            </div>

            {showSuggestions && suggestions.length > 0 && (
              <div className='absolute z-10 w-full mt-1 bg-white rounded-xl border border-slate-200 shadow-xl max-h-60 overflow-y-auto'>
                {suggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectSuggestion(suggestion)}
                    className='
                      w-full text-left px-4 py-3 text-sm
                      hover:bg-indigo-50 transition-colors
                      border-b border-slate-100 last:border-b-0
                      flex items-start gap-3
                    '
                  >
                    <FontAwesomeIcon icon={faMapMarkerAlt} className='text-indigo-400 mt-0.5 shrink-0' />
                    <div>
                      <span className='font-medium text-slate-800'>
                        {suggestion.address1}
                        {suggestion.address2 ? `, ${suggestion.address2}` : ''}
                      </span>
                      <span className='block text-slate-500'>
                        {suggestion.city}, {suggestion.state} {suggestion.zip}, {suggestion.country}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {showSuggestions && suggestions.length === 0 && !searching && searchQuery.trim().length >= 3 && (
              <div className='absolute z-10 w-full mt-1 bg-white rounded-xl border border-slate-200 shadow-xl px-4 py-3'>
                <p className='text-sm text-slate-500 text-center'>No addresses found. Enter the address manually below.</p>
              </div>
            )}
          </div>

          <div className='relative flex items-center'>
            <div className='flex-grow border-t border-slate-200' />
            <span className='mx-3 text-xs text-slate-400 uppercase tracking-wider'>or enter manually</span>
            <div className='flex-grow border-t border-slate-200' />
          </div>

          {/* Manual Fields */}
          <div className='space-y-3'>
            <InputText
              label='Street Address'
              labelRequired
              onChange={(val) => setAddress1(val || '')}
              placeholder='e.g., 123 Main St'
              value={address1}
            />
            <InputText
              label='Address Line 2'
              onChange={(val) => setAddress2(val || '')}
              placeholder='Suite, unit, floor, etc.'
              value={address2}
            />
            <Grid>
              <GridItem md={4}>
                <InputText
                  label='City'
                  labelRequired
                  onChange={(val) => setCity(val || '')}
                  placeholder='e.g., Denver'
                  value={city}
                />
              </GridItem>
              <GridItem md={3}>
                <InputText
                  label='State'
                  labelRequired
                  onChange={(val) => setState(val || '')}
                  placeholder='e.g., CO'
                  value={state}
                />
              </GridItem>
              <GridItem md={2}>
                <InputText
                  label='Zip Code'
                  labelRequired
                  onChange={(val) => setZip(val || '')}
                  placeholder='e.g., 80202'
                  value={zip}
                />
              </GridItem>
              <GridItem md={3}>
                <InputText
                  label='Country'
                  labelRequired
                  onChange={(val) => setCountry(val || '')}
                  placeholder='e.g., US'
                  value={country}
                />
              </GridItem>
            </Grid>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color='danger' icon={theme.icons.cancel} onClick={onClose}>
          Cancel
        </Button>
        <Button color='primary' icon={faCheck} onClick={handleSave}>
          Save Address
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export { ModalChangeAddress }
